//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

//Local libs
import MarketConnectModel from './market-connect.model';
import Service from '../common/service';

/**
 * Service for managing Market Connect related data.
 *
 * Provides methods to fetch market connectivity counts and graph data.
 *
 * @module MarketConnectService
 */
@Injectable({
  providedIn: 'root'
})
export class MarketConnectService extends Service<MarketConnectModel> {

  /**
   * Array to store ongoing HTTP request subscriptions to allow cancellation.
   * @type {Subscription[]}
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of MarketConnectService.
   * 
   * @param {HttpClient} httpClient - HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/marketconnectcounts/');
  }

  /**
   * Retrieves market connectivity counts for a specific date.
   *
   * @param {string} date - The date for which to retrieve market connectivity counts (format: YYYY-MM-DD).
   * @returns {Promise<Array<MarketConnectModel>>} Promise resolving to an array of MarketConnectModel objects.
   * @throws {HttpErrorResponse} If there is an HTTP request error.
   *
   * @see {@link MarketConnectComponent} for usage context.
   */
  public getMarketConnectCountsByDate(date: string): Promise<Array<MarketConnectModel>> {
    return new Promise<Array<MarketConnectModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<MarketConnectModel>>(`${this.baseUrl}${this.serviceUrl}receivedDate/${date}`, this.httpOptions)
        .subscribe({
          next: (response) => resolve(response),
          error: (error: HttpErrorResponse) => reject(error)
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Retrieves market connectivity graph data.
   *
   * @returns {Promise<Array<MarketConnectModel>>} Promise resolving to an array of MarketConnectModel objects representing graph data.
   * @throws {HttpErrorResponse} If there is an HTTP request error.
   *
   * @see {@link MarketConnectComponent} for usage context.
   */
  public getMarketConnectGraphData(): Promise<Array<MarketConnectModel>> {
    return new Promise<Array<MarketConnectModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<MarketConnectModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000`, this.httpOptions)
        .subscribe({
          next: (response) => resolve(response),
          error: (error: HttpErrorResponse) => reject(error)
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   *
   * This method unsubscribes from all active subscriptions in `ongoingRequests`.
   *
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
